<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import type { ComputedRef } from 'vue';
import { useRoute } from 'vue-router';
import { usePersonStore } from '@/stores/person';
import { useActivityLogger } from '@/composables/useActivityLogger';
import entityDataApi from '@/api/modules/entityDataApi';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import ProgressSpinner from 'primevue/progressspinner';
import Tag from 'primevue/tag';
import { formatTaxId, formatDate, formatMoneyRange } from '@/utils/formatters';
import { formatTimeAgo } from '@/utils/time';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import type { PersonBasicData, PersonAddress, AgeRange } from '@/types/person';
import type { OwnedCompaniesResponse } from '@/types/entity-data';
import type { ExtendedDataProductKey } from '@/api/modules/personApi';
import { DATA_UNIT_PRODUCT_KEYS } from '@/constants/dataUnitProducts';
import { useDataUnitPrices } from '@/composables/useDataUnitPrices';

import BaseIcon from '@/components/base/BaseIcon.vue';
import EntityLink from '@/components/base/EntityLink.vue';
import TokenActionConfirmationDialog from '@/components/common/TokenActionConfirmationDialog.vue';
import ContactInfoUnified from '@/components/person/ContactInfoUnified.vue';
import PersonTaxIdStatusBadge from '@/components/person/PersonTaxIdStatusBadge.vue';

const route = useRoute();
const personStore = usePersonStore();
const { logView } = useActivityLogger();

const uuid = computed(() => route.params.uuid as string);

const personData: ComputedRef<PersonBasicData | null> = computed(() =>
  (personStore.generalData && !personStore.error) ? personStore.generalData : null
);

const error: ComputedRef<string | null> = computed(() =>
  (personStore.error) ? personStore.error : null
);

// Get first/main address for map display
const mainAddress = computed((): PersonAddress | null => {
  if (!personData.value?.addresses || personData.value.addresses.length === 0) return null;
  // Prefer main address, otherwise first one
  const main = personData.value.addresses.find(a => a.isMain);
  return main ?? personData.value.addresses[0] ?? null;
});

const mapQuery = computed(() => {
  if (!mainAddress.value) return '';
  const { street, number, city, state, cep } = mainAddress.value;
  return encodeURIComponent(`${street}, ${number}, ${city}, ${state}, ${cep}`);
});

const hasAddressData = computed(() => personStore.hasAddressData);
const hasPhonesData = computed(() => personStore.hasPhonesData);
const hasEmailsData = computed(() => personStore.hasEmailsData);

type StatusSeverity = 'success' | 'danger' | 'warning' | 'info';

const obitSeverity = (hasObit: boolean | undefined): StatusSeverity => {
  if (hasObit) return 'danger';
  return 'success';
};

const missingValueLabel = '-';

const formatNullableDate = (value: string | null): string => {
  return value ? formatDate(value) : missingValueLabel;
};

const formatNullableAge = (value: number | null): string => {
  return value === null ? missingValueLabel : `${value} anos`;
};

// Only the obit year is known, so the birthday may or may not have passed before the obit
const formatAgeAtObit = (ageAtObit: AgeRange | null): string => {
  if (!ageAtObit) return missingValueLabel;
  if (ageAtObit.min === ageAtObit.max) return formatNullableAge(ageAtObit.max);
  return `${ageAtObit.min} ou ${ageAtObit.max} anos`;
};

// Alternative ID type labels
const alternativeIdLabels: Record<string, string> = {
  'SocialSecurityNumber': 'NIS/PIS',
  'RG': 'RG',
  'CNH': 'CNH',
  'Passport': 'Passaporte',
};

const getAlternativeIdLabel = (type: string): string => {
  return alternativeIdLabels[type] || type;
};

// Request extended data
const extendedDataRequestError = ref<string | null>(null);

async function requestExtendedData(datasets: ExtendedDataProductKey[]) {
  extendedDataRequestError.value = null;
  try {
    await personStore.requestExtendedData(uuid.value, datasets);
  } catch (err: any) {
    console.error('Error requesting extended data:', err);
    extendedDataRequestError.value = getApiErrorMessage(
      err,
      'Não foi possível solicitar os dados de contato. Tente novamente.',
      { genericOnServerError: true },
    );
  }
}

const isRequestingAddresses = computed(() => personStore.isRequestingAddressData());
const isRequestingPhones = computed(() => personStore.isRequestingPhoneData());
const isRequestingEmails = computed(() => personStore.isRequestingEmailData());
const ownedCompaniesResponse = ref<OwnedCompaniesResponse | null>(null);
const ownedCompaniesLoading = ref(false);
const ownedCompaniesError = ref<string | null>(null);
const checkingOwnedCompaniesAccess = ref(false);
const ownedCompaniesAccess = ref<boolean | null>(null);
const ownedCompaniesStatusError = ref<string | null>(null);
const requestingOwnedCompaniesAccess = ref(false);
const showOwnedCompaniesConfirmDialog = ref(false);
const ownedCompanies = computed(() => ownedCompaniesResponse.value?.items ?? []);
const ownedCompaniesDataReference = computed(() => ownedCompaniesResponse.value?.data_reference_timestamp ?? null);
const {
  loading: ownedCompaniesPriceLoading,
  error: ownedCompaniesPriceError,
  loadDataUnitPrices,
  getDataUnitPriceLabel,
} = useDataUnitPrices();
const ownedCompaniesProductKey = DATA_UNIT_PRODUCT_KEYS.personOwnedCompanies;
const ownedCompaniesPriceLabel = computed(() => getDataUnitPriceLabel(ownedCompaniesProductKey));

// Income and assets: a data unit of its own, bought from the card itself
const {
  loading: financialDataPriceLoading,
  error: financialDataPriceError,
  loadDataUnitPrices: loadFinancialDataPrices,
  getDataUnitPriceLabel: getFinancialDataPriceLabel,
} = useDataUnitPrices();
const financialDataProductKey = DATA_UNIT_PRODUCT_KEYS.personFinancialData;
const financialDataPriceLabel = computed(() => getFinancialDataPriceLabel(financialDataProductKey));
const showFinancialDataConfirmDialog = ref(false);
const financialDataRequestError = ref<string | null>(null);

const monthlyIncomeLabel = computed(() => formatMoneyRange(personStore.financialData?.monthlyIncome));
const totalAssetsLabel = computed(() => formatMoneyRange(personStore.financialData?.totalAssets));
const isFinancialDataBusy = computed(() =>
  personStore.financialDataLoading || personStore.requestingFinancialData
);
// Bought data older than 6 months can be kept as is or bought again to update it
const isFinancialDataOutdated = computed(() =>
  !!personStore.financialDataAccess && !!personStore.financialData?.isOutdated
);
const canBuyFinancialData = computed(() =>
  personStore.financialDataAccess === false || isFinancialDataOutdated.value
);

const openFinancialDataConfirmDialog = (): void => {
  void loadFinancialDataPrices([financialDataProductKey]);
  showFinancialDataConfirmDialog.value = true;
};

const handleConfirmFinancialDataRequest = async () => {
  showFinancialDataConfirmDialog.value = false;
  financialDataRequestError.value = null;
  try {
    await personStore.requestFinancialData(uuid.value);
  } catch (err: any) {
    console.error('Error requesting financial data:', err);
    financialDataRequestError.value = getApiErrorMessage(
      err,
      'Não foi possível obter a renda e o patrimônio. Tente novamente.',
      { genericOnServerError: true },
    );
  }
};

async function fetchOwnedCompanies(id: string) {
  ownedCompaniesLoading.value = true;
  ownedCompaniesError.value = null;

  try {
    ownedCompaniesResponse.value = await entityDataApi.getOwnedCompanies('person', id);
  } catch (error: any) {
    ownedCompaniesResponse.value = null;
    ownedCompaniesError.value = getApiErrorMessage(error, 'Não foi possível carregar as empresas próprias.');
  } finally {
    ownedCompaniesLoading.value = false;
  }
}

async function checkOwnedCompaniesAccess(id: string) {
  checkingOwnedCompaniesAccess.value = true;
  ownedCompaniesStatusError.value = null;

  try {
    const response = await entityDataApi.getDatasetAccess('person', id, 'business_relationships');
    ownedCompaniesAccess.value = response.hasData;
  } catch (error: any) {
    ownedCompaniesAccess.value = null;
    ownedCompaniesStatusError.value = getApiErrorMessage(error, 'Não foi possível verificar o acesso às empresas próprias.');
  } finally {
    checkingOwnedCompaniesAccess.value = false;
  }
}

async function syncOwnedCompaniesSection(id: string) {
  ownedCompaniesResponse.value = null;
  ownedCompaniesError.value = null;

  await checkOwnedCompaniesAccess(id);

  if (ownedCompaniesAccess.value) {
    await fetchOwnedCompanies(id);
    return;
  }

  ownedCompaniesLoading.value = false;
}

const openOwnedCompaniesRequestDialog = (): void => {
  void loadDataUnitPrices([ownedCompaniesProductKey]);
  showOwnedCompaniesConfirmDialog.value = true;
};

const handleCancelOwnedCompaniesRequest = (): void => {
  showOwnedCompaniesConfirmDialog.value = false;
};

async function handleConfirmOwnedCompaniesRequest() {
  requestingOwnedCompaniesAccess.value = true;
  ownedCompaniesStatusError.value = null;

  try {
    await entityDataApi.requestRelationshipTreeAccess('person', uuid.value);
    await syncOwnedCompaniesSection(uuid.value);
  } catch (error: any) {
    ownedCompaniesStatusError.value = getApiErrorMessage(error, 'Não foi possível concluir a solicitação dos dados societários.');
  } finally {
    requestingOwnedCompaniesAccess.value = false;
    showOwnedCompaniesConfirmDialog.value = false;
  }
}

// Helper to fetch general data and log the view
async function fetchDataAndLog(id: string) {
  try {
    await Promise.all([
      personStore.fetchGeneralData(id),
      syncOwnedCompaniesSection(id),
      personStore.syncFinancialData(id),
    ]);
  } catch (error) {
    console.error('Error fetching person data:', error);
  } finally {
    // Log the view, prioritizing the actual Tax ID (CPF) over the UUID
    const entityId = personStore.generalData?.taxId || id;
    if (entityId) {
      logView({
        entity_type: 'person',
        entity_id: entityId,
        entity_label: personStore.generalData?.name || entityId
      });
    }
  }
  personStore.checkExtendedDataStatus(id);
}

// Fetch general data on mount (component only mounts after status check confirms data exists)
onMounted(async () => {
  if (uuid.value) {
    await fetchDataAndLog(uuid.value);
  }
});

// Watch uuid changes (when navigating between different persons while on this tab)
watch(uuid, async (newVal, oldVal) => {
  if (newVal && newVal !== oldVal) {
    financialDataRequestError.value = null;
    extendedDataRequestError.value = null;
    await fetchDataAndLog(newVal);
  }
});
</script>

<template>
  <div class="person-general-data-page">
    <!-- Loading state -->
    <div v-if="personStore.generalData === null">
      <!-- Replace with: Skeleton that matches the final layout -->
      <div v-if="personStore.loading" class="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
        <div class="bg-white p-6 rounded-xl border border-gray-200">
          <div class="w-2/5 h-5 mb-6 rounded bg-gray-200 animate-pulse"></div>
          <div class="w-full h-4 mb-3 rounded bg-gray-100 animate-pulse"></div>
          <div class="w-4/5 h-4 mb-3 rounded bg-gray-100 animate-pulse"></div>
          <div class="w-3/5 h-4 rounded bg-gray-100 animate-pulse"></div>
        </div>
        <div class="bg-white p-6 rounded-xl border border-gray-200">
          <div class="w-1/3 h-5 mb-6 rounded bg-gray-200 animate-pulse"></div>
          <div class="w-11/12 h-4 mb-3 rounded bg-gray-100 animate-pulse"></div>
          <div class="w-3/4 h-4 rounded bg-gray-100 animate-pulse"></div>
        </div>
      </div>

      <!-- No general data available -->
      <div v-else class="error-message">
        <Card>
          <template #title>Dados não disponíveis</template>
          <template #content>
            <p>Não encontramos dados gerais para o CPF informado.</p>
          </template>
        </Card>
      </div>
    </div>


    <!-- Error state -->
    <div v-else-if="error" class="error-message">
      <Card>
        <template #title>Erro ao carregar dados</template>
        <template #content>
          <p>{{ error }}</p>
        </template>
      </Card>
    </div>

    <!-- Normal data view -->
    <div v-else-if="personData" class="person-dashboard">
      <div class="dashboard-grid">
        <div class="cards-columns">
          <div class="cards-column">
            <!-- Personal Information Card -->
            <div class="info-card">
              <div class="info-card-header">
                <span class="material-icons">person</span>
                <h3>Informações Pessoais</h3>
              </div>
              <div class="info-grid">
                <div class="info-item">
                  <span class="info-label"><span class="material-icons">badge</span>CPF</span>
                  <span class="info-value">{{ formatTaxId(personData.taxId) }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label"><span class="material-icons">label</span>Nome Conhecido</span>
                  <span class="info-value">{{ personData.commonName ?? missingValueLabel }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label"><span class="material-icons">event</span>Data de Nascimento</span>
                  <span class="info-value">{{ formatNullableDate(personData.birthDate) }}</span>
                </div>
                <div class="info-item" v-if="personData.obitIndication?.hasObitIndication">
                  <span class="info-label"><span class="material-icons">cake</span>Idade no Óbito</span>
                  <span class="info-value">{{ formatAgeAtObit(personData.obitIndication.ageAtObit) }}</span>
                </div>
                <div class="info-item" v-else>
                  <span class="info-label"><span class="material-icons">cake</span>Idade</span>
                  <span class="info-value">{{ formatNullableAge(personData.age) }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label"><span class="material-icons">wc</span>Sexo</span>
                  <span class="info-value">{{ personData.gender ?? missingValueLabel }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label"><span class="material-icons">public</span>Nacionalidade</span>
                  <span class="info-value">{{ personData.birthCountry ?? missingValueLabel }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label"><span class="material-icons">favorite</span>Estado Civil</span>
                  <span class="info-value">{{ personData.maritalStatus ?? missingValueLabel }}</span>
                </div>
                <div class="info-item" v-if="personData.obitIndication">
                  <span class="info-label"><span class="material-icons">warning</span>Indicação de Óbito</span>
                  <span class="info-value">
                    <Tag :value="personData.obitIndication.hasObitIndication ? 'SIM' : 'NÃO'"
                      :severity="obitSeverity(personData.obitIndication.hasObitIndication)" />
                    <span v-if="personData.obitIndication.obitYear" class="ml-2">({{ personData.obitIndication.obitYear
                      }})</span>
                  </span>
                </div>
              </div>
            </div>

            <!-- Contact Card -->
            <ContactInfoUnified
              :person-data="personData"
              :has-address-data="hasAddressData"
              :has-phones-data="hasPhonesData"
              :has-emails-data="hasEmailsData"
              :is-requesting-addresses="isRequestingAddresses"
              :is-requesting-phones="isRequestingPhones"
              :is-requesting-emails="isRequestingEmails"
              :request-error="extendedDataRequestError"
              @request-extended-data="requestExtendedData"
            />
          </div>

          <div class="cards-column">
            <!-- CPF Status Card -->
            <div class="info-card">
              <div class="info-card-header">
                <span class="material-icons">verified_user</span>
                <h3>Situação do CPF</h3>
              </div>
              <div class="info-grid">
                <div class="info-item">
                  <span class="info-label"><span class="material-icons">check_circle</span>Status</span>
                  <span class="info-value">
                    <PersonTaxIdStatusBadge :status="personData.taxIdStatus.description" />
                  </span>
                </div>
                <div class="info-item">
                  <span class="info-label"><span class="material-icons">location_on</span>Região Fiscal</span>
                  <span class="info-value">{{ personData.taxIdStatus.fiscalRegion ?? missingValueLabel }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label"><span class="material-icons">source</span>Origem</span>
                  <span class="info-value">{{ personData.taxIdStatus.origin ?? missingValueLabel }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label"><span class="material-icons">calendar_today</span>Data de Inscrição</span>
                  <span class="info-value">{{ formatNullableDate(personData.taxIdStatus.registrationDate) }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label"><span class="material-icons">update</span>Data do Status</span>
                  <span class="info-value">{{ formatNullableDate(personData.taxIdStatus.statusDate) }}</span>
                </div>
              </div>
            </div>

            <!-- Income and Assets Card -->
            <div class="info-card">
              <div class="info-card-header">
                <span class="material-icons">savings</span>
                <h3>Renda e Patrimônio</h3>
              </div>

              <div v-if="isFinancialDataBusy" class="financial-data-loading">
                <ProgressSpinner style="width: 32px; height: 32px" strokeWidth="4" />
              </div>

              <template v-else>
                <div v-if="personStore.financialDataAccess && personStore.financialData" class="info-grid">
                  <div class="info-item">
                    <span class="info-label"><span class="material-icons">payments</span>Renda estimada</span>
                    <span class="info-value">{{ monthlyIncomeLabel ? `${monthlyIncomeLabel}/mês` : missingValueLabel }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label"><span class="material-icons">account_balance</span>Patrimônio</span>
                    <span class="info-value">{{ totalAssetsLabel ?? missingValueLabel }}</span>
                  </div>
                </div>

                <div v-if="canBuyFinancialData" class="financial-data-action">
                  <p v-if="personStore.financialDataAccess === false" class="financial-data-hint">
                    Renda mensal estimada e patrimônio total desta pessoa, em faixas de valores.
                  </p>
                  <Button
                    :label="isFinancialDataOutdated ? 'Atualizar dados' : 'Comprar dados'"
                    size="small"
                    @click="openFinancialDataConfirmDialog"
                  >
                    <template #icon>
                      <BaseIcon>{{ isFinancialDataOutdated ? 'refresh' : 'shopping_cart' }}</BaseIcon>
                    </template>
                  </Button>
                  <p v-if="financialDataRequestError" class="financial-data-error">
                    {{ financialDataRequestError }}
                  </p>
                </div>

                <!-- The access check failed, so we know neither the data nor whether it was bought -->
                <p v-else-if="personStore.financialDataAccess === null" class="financial-data-hint">
                  Não foi possível carregar a renda e o patrimônio desta pessoa.
                </p>
              </template>
            </div>

            <!-- Filiation Card -->
            <div class="info-card" v-if="personData.motherName || personData.fatherName">
              <div class="info-card-header">
                <span class="material-icons">family_restroom</span>
                <h3>Filiação</h3>
              </div>
              <div class="info-grid">
                <div class="info-item" v-if="personData.fatherName">
                  <span class="info-label"><span class="material-icons">man</span>Nome do Pai</span>
                  <span class="info-value">{{ personData.fatherName }}</span>
                </div>
                <div class="info-item" v-if="personData.motherName">
                  <span class="info-label"><span class="material-icons">woman</span>Nome da Mãe</span>
                  <span class="info-value">{{ personData.motherName }}</span>
                </div>
              </div>
            </div>

            <!-- Alternative IDs Card -->
            <div class="info-card" v-if="personData.alternativeIds && personData.alternativeIds.length > 0">
              <div class="info-card-header">
                <span class="material-icons">fingerprint</span>
                <h3>Documentos Alternativos</h3>
              </div>
              <div class="info-grid">
                <div class="info-item" v-for="altId in personData.alternativeIds" :key="altId.type + altId.value">
                  <span class="info-label"><span class="material-icons">credit_card</span>{{
                    getAlternativeIdLabel(altId.type) }}</span>
                  <span class="info-value">{{ altId.value }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Owned companies: full width, it holds a table -->
        <div class="info-card owned-companies-card">
          <div class="">
            <div class="flex items-center gap-2 mb-4">
              <BaseIcon class="text-primary">business_center</BaseIcon>
                <div class="flex items-start justify-between gap-4 w-full">
                  <div>
                    <h3 class="font-semibold text-base">Empresas Próprias</h3>
                    <p class="text-sm text-gray-500">
                      Participações societárias ativas vinculadas a este CPF.
                    </p>
                  </div>

                  <Button
                    v-if="ownedCompaniesAccess === false"
                    label="Solicitar"
                    :loading="requestingOwnedCompaniesAccess"
                    size="small"
                    text
                    class="request-data-btn-inline w-fit"
                    @click="openOwnedCompaniesRequestDialog"
                  >
                    <template #icon>
                      <BaseIcon>download</BaseIcon>
                    </template>
                  </Button>

                  <div v-else class="owned-companies-header__meta">
                    <span class="owned-companies-count">
                      {{ ownedCompanies.length }} {{ ownedCompanies.length === 1 ? 'empresa ativa' : 'empresas ativas' }}
                    </span>
                    <span v-if="ownedCompaniesDataReference" class="owned-companies-data-reference">
                      Atualizado em {{ formatDate(ownedCompaniesDataReference) }}
                    </span>
                  </div>

                </div>
            </div>
          </div>

          <div v-if="checkingOwnedCompaniesAccess" class="owned-companies-state">
            <ProgressSpinner style="width: 28px; height: 28px" strokeWidth="4" />
            <div>
              <p class="owned-companies-state__title">Verificando acesso</p>
              <p class="owned-companies-state__description">
                Estamos validando se os dados societários já foram adquiridos para esta pessoa.
              </p>
            </div>
          </div>

          <div
            v-else-if="ownedCompaniesStatusError && ownedCompaniesAccess === null"
            class="owned-companies-status-panel owned-companies-status-panel--error"
          >
            <div class="owned-companies-status-panel__copy">
              <BaseIcon class="owned-companies-status-panel__icon">error</BaseIcon>
              <div>
                <p class="owned-companies-status-panel__title">Não foi possível verificar os dados</p>
                <p class="owned-companies-status-panel__description">{{ ownedCompaniesStatusError }}</p>
              </div>
            </div>

            <Button label="Tentar novamente" text class="w-fit" @click="syncOwnedCompaniesSection(uuid)">
              <template #icon>
                <BaseIcon>refresh</BaseIcon>
              </template>
            </Button>
          </div>

          <div v-else-if="ownedCompaniesAccess === false" class="owned-companies-status-panel">
            <div class="owned-companies-status-panel__copy">
              <BaseIcon class="owned-companies-status-panel__icon">account_tree</BaseIcon>
              <div>
                <p class="owned-companies-status-panel__title">Dados societários não disponíveis</p>
                <p class="owned-companies-status-panel__description">
                  Os dados de Empresas Próprias desta pessoa não estão disponíveis no momento. Solicite-os para visualização.
                </p>
              </div>
            </div>

            <p v-if="ownedCompaniesStatusError" class="owned-companies-inline-error">
              {{ ownedCompaniesStatusError }}
            </p>
          </div>

          <div v-else-if="ownedCompaniesLoading" class="owned-companies-loading-grid">
            <div v-for="index in 3" :key="`owned-company-skeleton-${index}`" class="owned-companies-skeleton">
              <div class="owned-companies-skeleton__line owned-companies-skeleton__line--lg"></div>
              <div class="owned-companies-skeleton__line owned-companies-skeleton__line--md"></div>
              <div class="owned-companies-skeleton__line owned-companies-skeleton__line--sm"></div>
            </div>
          </div>

          <div v-else-if="ownedCompaniesError" class="owned-companies-inline-error">
            {{ ownedCompaniesError }}
          </div>

          <div v-else-if="ownedCompanies.length === 0" class="owned-companies-empty">
            Nenhuma empresa própria ativa foi encontrada para este CPF.
          </div>

          <div v-else class="owned-companies-table-wrap">
            <DataTable
              :value="ownedCompanies"
              responsiveLayout="scroll"
              scrollable
              scrollHeight="18rem"
              removableSort
              sortField="company_name"
              :sortOrder="1"
              class="text-xs"
            >
              <Column field="company_name" header="Empresa" sortable style="width: 56%; min-width: 22rem">
                <template #body="{ data }">
                  <EntityLink
                    :tax-id="data.cnpj"
                    :name="data.company_name || 'Empresa sem nome informado'"
                    type="company"
                  />
                </template>
              </Column>

              <Column field="relationship_name" header="Relação" sortable style="width: 18%; min-width: 10rem">
                <template #body="{ data }">
                  <span class="owned-companies-relationship-pill">
                    {{ data.relationship_name || 'Não informado' }}
                  </span>
                </template>
              </Column>

              <Column field="relationship_start_date" header="Início da relação" sortable style="width: 26%; min-width: 11rem">
                <template #body="{ data }">
                  <span class="font-semibold">
                    {{ formatDate(data.relationship_start_date, undefined, 'Não informado') }}
                  </span>
                  <span v-if="data.relationship_start_date" class="block text-xs text-gray-400">
                    {{ formatTimeAgo(data.relationship_start_date) }}
                  </span>
                </template>
              </Column>
            </DataTable>
          </div>
        </div>

        <!-- Map Section -->
        <div v-if="mainAddress" class="info-card full-width-card">
          <div class="info-card-header">
            <span class="material-icons">map</span>
            <h3>Localização</h3>
            <span class="ml-auto text-xs text-gray-500">{{ mainAddress.street }}, {{ mainAddress.number }} - {{ mainAddress.city }}/{{ mainAddress.state }}</span>
          </div>
          <iframe
            class="location-map"
            loading="lazy"
            allowfullscreen
            :src="`https://maps.google.com/maps?q=${mapQuery}&z=15&output=embed`"
          >
          </iframe>
        </div>
      </div>
    </div>
  </div>

  <TokenActionConfirmationDialog
    v-model:visible="showOwnedCompaniesConfirmDialog"
    title="Confirmar Solicitação"
    message-prefix="Você está prestes a solicitar os dados societários desta pessoa."
    message-suffix=""
    :cost-label="ownedCompaniesPriceLabel"
    :cost-error="ownedCompaniesPriceError"
    :cost-loading="ownedCompaniesPriceLoading"
    confirm-label="Solicitar"
    @confirm="handleConfirmOwnedCompaniesRequest"
    @cancel="handleCancelOwnedCompaniesRequest"
  />

  <TokenActionConfirmationDialog
    v-model:visible="showFinancialDataConfirmDialog"
    title="Confirmar compra"
    :message-prefix="isFinancialDataOutdated
      ? 'Você está prestes a comprar novamente a renda estimada e o patrimônio desta pessoa, para atualizá-los.'
      : 'Você está prestes a comprar a renda estimada e o patrimônio desta pessoa.'"
    message-suffix=""
    :cost-label="financialDataPriceLabel"
    :cost-error="financialDataPriceError"
    :cost-loading="financialDataPriceLoading"
    :confirm-disabled="financialDataPriceLoading || !financialDataPriceLabel"
    confirm-label="Comprar"
    @confirm="handleConfirmFinancialDataRequest"
    @cancel="showFinancialDataConfirmDialog = false"
  />
</template>

<style scoped>
.person-general-data-page {
  /* padding: 2rem;
  background-color: #f8f9fa; */
  height: 100%;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

/* Two independent stacks instead of rows: every card keeps its own height,
   so no card is stretched and no card waits for a taller neighbour */
.cards-columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.5rem;
  align-items: start;
}

.cards-column {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 0;
}

@media (max-width: 1100px) {
  .cards-columns {
    grid-template-columns: minmax(0, 1fr);
  }
}

.financial-data-loading {
  display: flex;
  justify-content: center;
  padding: 1.5rem 0;
}

.financial-data-action {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
}

.financial-data-hint {
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
}

.financial-data-error {
  margin: 0;
  color: #b42318;
  font-size: 0.85rem;
}

.info-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #e9ecef;
}

.info-card.full-width-card {
  grid-column: 1 / -1;
}

.info-card-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #343a40;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f1f3f5;
}

.info-card-header .material-icons {
  font-size: 1.5rem;
  color: var(--primary-color, #007bff);
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

.info-item {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 1rem;
  padding: 0.5rem 0;
  align-items: center;
}

.info-label {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  font-weight: 500;
  color: #6c757d;
  font-size: 0.875rem;
}

.info-label .material-icons {
  font-size: 1.1rem;
  color: #adb5bd;
}

.info-value {
  color: #212529;
  font-size: 1rem;
  font-weight: 600;
}

.owned-companies-card {
  padding: 1.35rem;
}

.owned-companies-header {
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.owned-companies-header__title {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  min-width: 0;
}


.owned-companies-header__title h3 {
  margin: 0;
}

.owned-companies-header__subtitle {
  margin: 0.35rem 0 0;
  color: #6b7280;
  font-size: 0.85rem;
  font-weight: 500;
}

.owned-companies-header__meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.45rem;
  margin-left: auto;
}

.owned-companies-count {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 0.4rem 0.75rem;
  text-transform: uppercase;
}

.owned-companies-data-reference {
  color: #6b7280;
  font-size: 0.75rem;
}

.owned-companies-state {
  display: flex;
  align-items: center;
  gap: 1rem;
  border: 1px dashed #d1d5db;
  border-radius: 14px;
  background: #f9fafb;
  padding: 1.25rem;
}

.owned-companies-state__title {
  margin: 0;
  color: #111827;
  font-size: 0.95rem;
  font-weight: 700;
}

.owned-companies-state__description {
  margin: 0.35rem 0 0;
  color: #6b7280;
  font-size: 0.85rem;
}

.owned-companies-status-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  border: 1px dashed #d1d5db;
  border-radius: 14px;
  background: #f9fafb;
  padding: 1.25rem;
}

.owned-companies-status-panel--error {
  border-color: #fecaca;
  background: #fff5f5;
}

.owned-companies-status-panel__copy {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
}

.owned-companies-status-panel__icon {
  color: #6b7280;
  flex-shrink: 0;
  font-size: 1.3rem;
}

.owned-companies-status-panel--error .owned-companies-status-panel__icon {
  color: #dc2626;
}

.owned-companies-status-panel__title {
  margin: 0;
  color: #111827;
  font-size: 0.95rem;
  font-weight: 700;
}

.owned-companies-status-panel__description {
  margin: 0.35rem 0 0;
  color: #6b7280;
  font-size: 0.85rem;
  line-height: 1.5;
}

.owned-companies-inline-error {
  border: 1px solid #fecaca;
  border-radius: 12px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 0.85rem;
  padding: 0.9rem 1rem;
}

.request-data-btn-inline {
  margin-left: auto;
  padding: 0.15rem 0.5rem !important;
  font-size: 0.65rem !important;
  height: auto !important;
}

.request-data-btn-inline :deep(.p-button-icon) {
  font-size: 0.7rem !important;
}

.request-data-btn-inline :deep(.p-button-label) {
  font-weight: 500;
}

.owned-companies-loading-grid {
  display: grid;
  gap: 0.85rem;
}

.owned-companies-skeleton {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #ffffff;
  padding: 1rem;
}

.owned-companies-skeleton__line {
  border-radius: 999px;
  background: linear-gradient(90deg, #e2e8f0 0%, #f8fafc 50%, #e2e8f0 100%);
  background-size: 200% 100%;
  animation: pulse-slide 1.6s ease-in-out infinite;
  height: 0.8rem;
}

.owned-companies-skeleton__line + .owned-companies-skeleton__line {
  margin-top: 0.7rem;
}

.owned-companies-skeleton__line--lg {
  width: 56%;
}

.owned-companies-skeleton__line--md {
  width: 38%;
}

.owned-companies-skeleton__line--sm {
  width: 28%;
}

.owned-companies-empty {
  color: #adb5bd;
  font-size: 0.85rem;
  font-style: italic;
  padding: 0.25rem 0;
}

.owned-companies-table-wrap {
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  overflow: hidden;
  background: #ffffff;
  width: 100%;
}

.owned-companies-relationship-pill {
  display: inline-flex;
  align-items: center;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: #f9fafb;
  color: #4b5563;
  font-size: 0.65rem;
  font-weight: 600;
  padding: 0.32rem 0.7rem;
}

.location-map {
  border: 0;
  border-radius: 8px;
  height: 360px;
  width: 100%;
}

.info-value .p-tag {
  font-size: 0.875rem;
  padding: 0.25rem 0.5rem;
}

.loading-message,
.error-message {
  max-width: 500px;
  margin: 4rem auto;
  text-align: center;
  background: #fff;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #6c757d;
  font-style: italic;
}

/* Utility classes */
.ml-2 {
  margin-left: 0.5rem;
}

.mr-2 {
  margin-right: 0.5rem;
}

@keyframes pulse-slide {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 900px) {
  .owned-companies-header {
    flex-direction: column;
  }

  .owned-companies-header__meta {
    align-items: flex-start;
    margin-left: 0;
  }

  .location-map {
    height: 300px;
  }
}
</style>
